import { inject, observer } from 'mobx-react';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Card, CardBody, CardHeader, Col, Container, Row } from 'reactstrap';

import useAlerts from '../../../hooks/order/useAlerts';
import globalTranslations from '../../../i18n/globalTranslations';
import Order from '../../../models/order/Order';
import ProductImageModel from '../../../models/ProductImage';
import { modelOf } from '../../../prop-types';
import AccountStore from '../../../store/AccountStore';
import ConfigStore from '../../../store/ConfigStore';
import UIStore from '../../../store/UIStore';
import ProductTypeClass from '../../../types/ProductTypeClass';
import MaybeExternalLink from '../../common/MaybeExternalLink';
import OrderTotals from '../../order/OrderTotals';
import ProductImage from '../../product/ProductImage';
import ProductWarranty from '../../product/ProductWarranty';
import { OrderHeaders } from '../MyAccountOrder';
import OrderCloneButton from "../../order/OrderCloneButton";
import RequestState from "../../../types/RequestState";
import CartStore from "../../../store/CartStore";

const MyAccountOrderProducts = ({
  accountStore,
  cartStore,
  configStore,
  uiStore,
  order,
}) => {
  const { isMobile } = uiStore;
  const { commonAlerts } = order;

  const { renderGeneralAlerts } = useAlerts({
    generalAlerts: commonAlerts.general,
  });

  const renderProductHeaderCol = (label, sizes) => {
    const actualSizes = Object.assign(
      {
        xs: 4,
      },
      sizes
    );
    return (
      <Col className="MyAccountOrder__product-header" {...actualSizes}>
        {label}
      </Col>
    );
  };

  const renderProductLink = (product) => {
    return product.isPhysicalProduct && product.url ? (
      <MaybeExternalLink to={product.url}>
        {product.name}
      </MaybeExternalLink>
    ) : (
      <span className="MyAccountOrder__products-name">{product.name}</span>
    );
  };

  const renderProductCode = (product) => {
    if (configStore.productCard.showProductCode || accountStore.isRetailer) {
      return (
        <span className="MyAccountOrder__products-code">
          {product.productCode}
        </span>
      );
    }
  };

  const createProductImage = (url, id) => {
    return url
      ? ProductImageModel.create({
          id: 0,
          product_id: id,
          sizes: {
            small: url,
          },
        })
      : null;
  };

  const renderProductRow = (product) => {
    return (
      <Row key={product.id} className="MyAccountOrder__product-row">
        {!isMobile && (
          <Col className="MyAccountOrder__products-image" xs="2" xl="1">
            {product.isPhysicalProduct && (
              <MaybeExternalLink to={product.url}>
                <ProductImage
                  product={product}
                  productImage={createProductImage(
                    product.image_url,
                    product.id
                  )}
                  size="small"
                  forceAspectRatio={false}
                />
              </MaybeExternalLink>
            )}
          </Col>
        )}
        {isMobile && renderProductHeaderCol(OrderHeaders.name)}
        <Col xs="8" md="6" xl="7">
          <div className="MyAccountOrder__products-name-wrapper">
            {renderProductLink(product)}
            {product &&
              product.type !== ProductTypeClass.SHIPPING_COST &&
              renderProductCode(product)}
          </div>
          {product.variations && product.variations.length > 0 && (
            <ul>
              {product.variations.map((variation, index) => (
                <li key={index}>{variation}</li>
              ))}
            </ul>
          )}
          {product.reference && (
            <div className="MyAccountOrder__products-reference">
              <FormattedMessage {...globalTranslations.yourReferenceTitle} />
              {': ' + product.reference}
            </div>
          )}
          {product.warranty_in_months > 0 && (
            <div className="MyAccountOrder__products-warranty">
              <ProductWarranty warranty={product.warranty_in_months} />
            </div>
          )}
        </Col>
        {product.isPhysicalProduct ? (
          <>
            {isMobile && renderProductHeaderCol(OrderHeaders.quantity)}
            <Col xs="8" md="1">
              {product.quantity_ordered} {product.stock_unit}
            </Col>
            {isMobile && renderProductHeaderCol(OrderHeaders.deliveredQuantity)}
            <Col xs="8" md="1">
              {product.quantity_delivered} {product.stock_unit}
            </Col>
            {isMobile && renderProductHeaderCol(OrderHeaders.unitPrice)}
            <Col
              xs="8"
              md="1"
              dangerouslySetInnerHTML={{ __html: product.unit_price_formatted }}
            />
          </>
        ) : !isMobile ? (
          <Col md="3" />
        ) : null}
        {isMobile && renderProductHeaderCol(OrderHeaders.totalSum)}
        <Col
          xs="8"
          md="1"
          dangerouslySetInnerHTML={{ __html: product.total_price_formatted }}
        />
      </Row>
    );
  };

  const renderProductHeaderRow = () => {
    return (
      <Row className="MyAccountOrder__product-headers">
        {renderProductHeaderCol(OrderHeaders.image, { md: 2, xl: 1 })}
        {renderProductHeaderCol(OrderHeaders.name, { md: 6, xl: 7 })}
        {renderProductHeaderCol(OrderHeaders.quantity, { md: 1 })}
        {renderProductHeaderCol(OrderHeaders.deliveredQuantity, { md: 1 })}
        {renderProductHeaderCol(OrderHeaders.unitPrice, { md: 1 })}
        {renderProductHeaderCol(OrderHeaders.totalSum, { md: 1 })}
      </Row>
    );
  };

  return (
    <Row>
      {commonAlerts?.general?.length > 0 && (
        <Col xs={12}>{renderGeneralAlerts()}</Col>
      )}
      <Col xs={12}>
        <Card className="MyAccountOrderProducts">
          <CardHeader>
            <FormattedMessage {...globalTranslations.productsTitle} />
            <OrderCloneButton
              order={order}
              disabled={!cartStore.state === RequestState.LOADING}
            />
          </CardHeader>
          <CardBody>
            <Container className="MyAccountOrder__products-container">
              <div className="MyAccountOrder__products">
                {!uiStore.isMobile && renderProductHeaderRow()}
                {order.products.map((product) => renderProductRow(product))}
              </div>
              <Row className="MyAccountOrder__products-totals-row">
                <Col md="9" />
                <Col>
                  <OrderTotals totals={order.allTotals} />
                </Col>
              </Row>
            </Container>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

MyAccountOrderProducts.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  cartStore: modelOf(CartStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  order: modelOf(Order).isRequired,
};

export default inject(
  'accountStore',
  'cartStore',
  'configStore',
  'uiStore'
)(observer(MyAccountOrderProducts));
